<template>
    <AppLoadingContainer :predicate="isLoading">
        <div class="card-body">
          <transition name="fade">
              <AppAlertNote>
                  Rooms/Suites tied to another room/suite or to a lock must have the associated records removed first before deleting.
              </AppAlertNote>
          </transition>
          <div class="mb-2 text-right">
              <button @click="addModalOpen=true" class="btn btn-primary d-print-none" data-toggle="modal"><i
                      class="fa fa-plus"></i>Add Rooms/Suites
              </button>
          </div>
          <TheBuildingSpaceAddForm :building-id="buildingId" 
                                  :visible="addModalOpen"
                                  @close="onClose()"
                                  @create="onCreate()"/>
          <TheBuildingSpaceEditForm v-if="editRecordId !== null"
                                    :building-id="buildingId"
                                    :id="editRecordId"
                                    :visible="editModalOpen"
                                    @close="onClose()"
                                    @create="onCreate()"/>

            <BTable
                :busy="isLoading"
                :fields="tableFields"
                :items="buildingSpaces"
                :responsive="true"
                primary-key="id"
                show-empty
                striped
                table-class="table-align-middle">

            <template v-slot:cell(id)="row">
                <div class="btn-group">
                    <button @click="editRecordId=row.item.id;editModalOpen=true;" class="btn btn-warning btn-sm d-print-none"
                            type="button"><i class="fa fa-pencil-alt"></i>Edit
                    </button>
                    <button @click="onDeleteClick(row.item)" class="btn btn-danger btn-sm d-print-none"
                            type="button"><i class="fa fa-trash"></i>Delete
                    </button>
                </div>
            </template>

            <template v-slot:cell(parentBuildingSpaceId)="row">
                <span>
                  {{ row.item.parentBuildingSpaceType }} {{ row.item.parentBuildingSpaceIdentity }}
                </span>
            </template>

            <!-- Footer Pagination -->
            <template v-slot:custom-foot>
                <b-tr>
                    <b-td :colspan="tableFields.length">
                        <AppTablePagination
                                :page.sync="filterOptions.page"
                                :pageSize.sync="filterOptions.pageSize"
                                :totalCount.sync="totalBuildingSpaces"
                                v-on:update:page="loadData()"
                                v-on:update:pageSize="loadData()"
                        ></AppTablePagination>
                    </b-td>
                </b-tr>
            </template>

        </BTable>
        <div class="card-footer d-print-none text-right">
            <RouterLink :to="{name:'Buildings'}" class="btn btn-danger">Back</RouterLink>
        </div>
        <AppModalConfirm :visible="deletingRow" @close="deletingRow=null" @confirm="onDeleteConfirmed()">
            <template v-slot:header>Confirm</template>
            <p v-if="deletingRow">Are you sure you want to delete {{deletingRow.spaceType}} {{deletingRow.spaceIdentity}}?</p>
        </AppModalConfirm>
    </AppLoadingContainer>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import AppAlertNote from "@/components/shared/AppAlertNote";
    import AppTablePagination from "../../components/shared/AppTablePagination";
    import BuildingSpaceService from "@/services/BuildingSpaceService";
    import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
    import TheBuildingSpaceAddForm from "@/components/buildings/TheBuildingSpaceAddForm";
    import TheBuildingSpaceEditForm from "@/components/buildings/TheBuildingSpaceEditForm";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";

    export default {
        components: {
            AppAlertNote,
            AppModalConfirm,
            AppTablePagination,
            AppLoadingContainer,
            TheBuildingSpaceAddForm,
            TheBuildingSpaceEditForm
        },
        data() {
            return {
                buildingSpaces: [],
                totalBuildingSpaces: 0,
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    buildingId: this.buildingId,
                    spaceType: null,
                    parentSpaceType: null,
                    spaceIdentity: null,
                    excludeBuildingSpaceId: null
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    }, {
                        key: 'spaceType',
                        label: 'Type'
                    },
                    {
                        key: 'spaceIdentity',
                        label: 'Number'
                    },
                    {
                        key: 'parentBuildingSpaceId',
                        label: 'Parent Room/Suite'
                    }
                ],
                deletingRow: null,
                addModalOpen: false,
                editModalOpen: false,
                editRecordId: null
            }
        },
        props: {
            buildingId: {
                type: String,
                required: false
            }
        },
        methods: {
            loadData() {
              const vm = this;
              this.isLoading = true
              BuildingSpaceService.getBuildingSpaces(this.buildingId, this.filterOptions)
                .then(response => {
                  vm.buildingSpaces = response.data;
                  vm.totalBuildingSpaces = response.totalCount;
                }).catch(() => {
                    vm.redirectToError()
                }).finally(() => vm.isLoading = false);
            },

            /**
             * @description Reloads the data
             */
            onCreate() {
                this.addModalOpen = false;
                this.editModalOpen = false;
                this.editRecordId = null;
                this.filterOptions.page = 1;
                this.loadData();
            },

            onClose() {
                this.addModalOpen = false;
                this.editModalOpen = false;
                this.editRecordId = null;
            },
            
            /**
             * @description Opens up a modal to confirm deletion of the building space
             */
            onDeleteClick(buildingSpace) {
                this.deletingRow = buildingSpace;
            },

            /**
             * @description When the user clicks 'Confirm' - the selected building space will be deleted and the modal will be closed.
             */
            onDeleteConfirmed() {
                const vm = this;
                BuildingSpaceService.deleteBuildingSpace(this.deletingRow.id)
                  .then(() => {
                      vm.alert(ToastType.Success, `${vm.deletingRow.spaceType} ${vm.deletingRow.spaceIdentity} was successfully deleted.`);
                      vm.filterOptions.page = 1;
                      vm.loadData();
                  }).catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)
                  })
                  .finally(() => {
                      vm.deletingRow = null;
                  });
            },
        },
        created() {
            this.loadData();
        }
    }
</script>
